import React, { useEffect, useRef, useState, useCallback } from 'react';

import LoadingSpinner from '../LoadingSpinner';

import DocumentHighlightOverlay, {
  type HighlightRect,
} from './DocumentHighlightOverlay';

// Cache the Promise (not result) to avoid duplicate imports under concurrency
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let pdfjsPromise: Promise<any> | null = null;

const getPdfjsLib = () => {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist/build/pdf').then(lib => {
      lib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${lib.version}/pdf.worker.min.js`;
      return lib;
    });
  }
  return pdfjsPromise;
};

interface PageDimensions {
  width: number;
  height: number;
}

interface PdfDocumentViewerProps {
  url: string;
  scale: number;
  highlightsByPage?: Map<number, HighlightRect[]>;
  onHighlightClick?: (id: string) => void;
}

/**
 * Renders a PDF document page-by-page using PDF.js canvases.
 * Each page has an overlay for highlight rectangles.
 */
const PdfDocumentViewer: React.FC<PdfDocumentViewerProps> = ({
  url,
  scale,
  highlightsByPage,
  onHighlightClick,
}) => {
  const [pageCount, setPageCount] = useState(0);
  const [pageDimensions, setPageDimensions] = useState<
    Map<number, PageDimensions>
  >(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pdfDocRef = useRef<any>(null);
  const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);

  // Load PDF document
  useEffect(() => {
    let cancelled = false;

    const loadPdf = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const pdfjs = await getPdfjsLib();
        const loadingTask = pdfjs.getDocument(url);
        const pdf = await loadingTask.promise;

        if (cancelled) return;

        pdfDocRef.current = pdf;
        setPageCount(pdf.numPages);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to load PDF'
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      pdfDocRef.current?.destroy();
      pdfDocRef.current = null;
    };
  }, [url]);

  // Render a single page to its canvas
  const renderPage = useCallback(
    async (pageNum: number) => {
      const pdf = pdfDocRef.current;
      const canvas = canvasRefs.current.get(pageNum);
      if (!pdf || !canvas) return;

      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale });
      const context = canvas.getContext('2d');
      if (!context) return;

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      setPageDimensions(prev => {
        const next = new Map(prev);
        next.set(pageNum, {
          width: viewport.width,
          height: viewport.height,
        });
        return next;
      });

      await page.render({ canvasContext: context, viewport }).promise;
    },
    [scale]
  );

  // Render all pages when document loads or scale changes
  useEffect(() => {
    if (pageCount === 0) return;
    let cancelled = false;

    const renderAll = async () => {
      for (let i = 1; i <= pageCount; i++) {
        if (cancelled) return;
        await renderPage(i);
      }
    };
    renderAll();

    return () => {
      cancelled = true;
    };
  }, [pageCount, renderPage]);

  const setCanvasRef = useCallback(
    (pageNum: number) => (el: HTMLCanvasElement | null) => {
      if (el) {
        canvasRefs.current.set(pageNum, el);
      }
    },
    []
  );

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <LoadingSpinner size='lg' />
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex items-center justify-center h-64 text-red-500 text-sm'>
        {error}
      </div>
    );
  }

  return (
    <div ref={containerRef} className='flex flex-col gap-2'>
      {Array.from({ length: pageCount }, (_, i) => i + 1).map(pageNum => {
        const dims = pageDimensions.get(pageNum);
        const highlights = highlightsByPage?.get(pageNum) ?? [];

        return (
          <div key={pageNum} className='relative inline-block'>
            <canvas ref={setCanvasRef(pageNum)} className='block' />
            {dims && (
              <DocumentHighlightOverlay
                highlights={highlights}
                containerWidth={dims.width}
                containerHeight={dims.height}
                onHighlightClick={onHighlightClick}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default PdfDocumentViewer;
